if (!customElements.get('nr-collection-accessories')) {
  class NRCollectionAccessories extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      this.productInfo = this.closest('product-info');
      this.button = this.querySelector('[data-nr-add]');
      this.buttonLabel = this.button?.textContent.trim();
      this.status = this.querySelector('[data-nr-status]');
      this.mainPrice = Number(this.dataset.mainPrice);
      this.mainId = this.dataset.mainId;
      this.addEventListener('change', () => this.updateTotal(), { signal });
      this.productInfo?.addEventListener('change', () => this.updateTotal(), { signal });
      document.addEventListener('variant:changed', () => this.updateTotal(), { signal });
      this.button?.addEventListener('click', () => this.addBundle(), { signal });
      this.quantityUnsubscribe?.();
      const quantityEvent = window.MinimogTheme?.pubSubEvents?.quantityUpdate;
      if (quantityEvent && window.MinimogEvents) {
        this.quantityUnsubscribe = window.MinimogEvents.subscribe(quantityEvent, () => this.updateTotal());
      }
      this.productInfo?.addEventListener('input', () => this.updateTotal(), { signal });
      window.addEventListener('syncProductQuantity', () => queueMicrotask(() => {
        if (this.isConnected) this.updateTotal();
      }), { signal });
      this.updateTotal();
    }
    disconnectedCallback() {
      this.abortController?.abort();
      this.quantityUnsubscribe?.();
    }
    get mainForm() {
      return document.getElementById(this.dataset.formId);
    }
    get mainQuantity() {
      const control = this.mainForm?.elements.namedItem('quantity') || this.productInfo?.quantityInput;
      return Number(control?.value || 1);
    }
    syncVariant() {
      const formId = this.mainForm?.elements.namedItem('id')?.value;
      const variantNode = this.productInfo?.querySelector('variant-picker [data-selected-variant]');
      if (variantNode) {
        try {
          const variant = JSON.parse(variantNode.textContent);
          if (variant && String(variant.id) === String(formId)) {
            this.mainPrice = Number(variant.price);
            this.mainId = String(variant.id);
          }
        } catch (error) {
          this.mainId = '';
        }
      }
      const submit = this.mainForm?.querySelector('[name="add"]');
      const valid = formId && String(formId) === String(this.mainId) && !submit?.disabled;
      if (this.button && !this.busy) this.button.disabled = !valid;
      return Boolean(valid);
    }
    formatPrice(amount) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.dataset.currency || 'CAD',
        currencyDisplay: 'code'
      }).format(amount / 100);
    }
    get selectedRows() {
      return [...this.querySelectorAll('[data-nr-row]')].filter(row => row.querySelector('[data-nr-check]')?.checked);
    }
    updateTotal() {
      this.syncVariant();
      let amount = this.mainPrice * this.mainQuantity;
      this.querySelectorAll('[data-nr-row]').forEach(row => {
        const option = row.querySelector('[data-nr-variant]')?.selectedOptions[0];
        if (!option) return;
        const price = Number(option.dataset.price);
        const display = row.querySelector('[data-nr-price]');
        if (display) display.textContent = this.formatPrice(price);
        if (row.querySelector('[data-nr-check]')?.checked) amount += price;
      });
      const total = this.querySelector('[data-nr-total]');
      if (total) total.textContent = this.formatPrice(amount);
    }
    showStatus(message, error = false) {
      if (!this.status) return;
      this.status.hidden = !message;
      this.status.textContent = message;
      this.status.dataset.error = String(error);
    }
    refreshCart(cart) {
      if (typeof cart?.sections?.['cart-drawer'] === 'string' && typeof cart.sections['cart-count'] === 'string' && window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { cart });
        const drawer = document.querySelector('m-cart-drawer');
        if (drawer) {
          drawer.setActiveElement?.(this.button);
          drawer.open();
          return;
        }
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      if (!document.querySelector('m-cart-drawer')) {
        window.location.assign((window.Shopify?.routes.root || '/') + 'cart');
      }
    }
    async addBundle() {
      if (this.busy) return;
      const form = this.mainForm;
      if (!form || !this.syncVariant()) {
        this.showStatus(this.dataset.selectMain, true);
        return;
      }
      if (!form.reportValidity()) return;
      const quantity = this.mainQuantity;
      if (!Number.isSafeInteger(quantity) || quantity < 1) return;
      const mainData = new FormData(form);
      const payload = new FormData();
      payload.append('items[0][id]', mainData.get('id'));
      payload.append('items[0][quantity]', String(quantity));
      if (mainData.get('selling_plan')) payload.append('items[0][selling_plan]', mainData.get('selling_plan'));
      for (const [key, value] of mainData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (!match) continue;
        if (typeof value !== 'string') {
          if (value.size > 0) {
            this.showStatus(this.dataset.fileHint, true);
            return;
          }
          continue;
        }
        payload.append('items[0][properties][' + match[1] + ']', value);
      }
      this.selectedRows.forEach((row, index) => {
        const select = row.querySelector('[data-nr-variant]');
        payload.append('items[' + (index + 1) + '][id]', select.value);
        payload.append('items[' + (index + 1) + '][quantity]', '1');
      });
      const sections = ['cart-drawer', 'cart-count'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections }
      }));
      const uniqueSections = [...new Set(sections)].slice(0, 5);
      if (uniqueSections.length) {
        payload.append('sections', uniqueSections.join(','));
        payload.append('sections_url', window.location.pathname);
      }
      this.busy = true;
      this.button.disabled = true;
      this.button.textContent = this.dataset.pending;
      this.showStatus('');
      try {
        const response = await fetch((window.Shopify?.routes.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: payload
        });
        const cart = await response.json();
        if (!response.ok || cart.status) {
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
          throw new Error(this.dataset.error);
        }
        this.showStatus(this.dataset.added);
        this.refreshCart(cart);
      } catch (error) {
        this.showStatus(this.dataset.error, true);
      } finally {
        this.busy = false;
        this.button.textContent = this.buttonLabel;
        this.updateTotal();
      }
    }
  }
  customElements.define('nr-collection-accessories', NRCollectionAccessories);
}


